import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { brand } from '../../config/brand';
import { api } from '../../lib/api';
import { useDocumentTitle } from '../../lib/title';
import { type LoginResponse, useSession } from '../../stores/session';
import { AuthLayout } from './AuthLayout';
import { clearReferral, pendingReferral } from './JoinPage';
import { useFormErrors } from './useFormErrors';

interface Ticket {
  provider: string;
  handle: string | null;
  email: string | null;
  suggested: string;
  name: string;
}

/**
 * `/finish-signup` - the half of a Discord sign-up that Discord cannot do.
 *
 * ## Why there is a screen here at all
 *
 * Because a username on this platform is not a detail, it is the address of a
 * profile and of a referral link, and it is permanent. Deriving one silently
 * from a Discord handle would hand people a name they did not choose and would
 * hand two people the same one. So "continue with Discord" is one click and
 * then one short form, and this page tries to be honest about that rather than
 * pretending the second step is not there.
 *
 * The terms are agreed to here for the same reason: an agreement collected by
 * somebody else's login screen is not an agreement to ours.
 *
 * ## The ticket is a reference, not the data
 *
 * Everything about who this person is stays on the server under the ticket. The
 * address shown below is read back for display and is not what gets submitted -
 * see `OAuthCompleteAction` on why an endpoint that believed a posted address
 * would be a way to make an account on anybody's.
 */
export function FinishSignupPage() {
  useDocumentTitle('Choose your name');

  const { search } = useLocation();
  const navigate = useNavigate();
  const adopt = useSession((state) => state.adopt);
  const errors = useFormErrors();

  const ticket = new URLSearchParams(search).get('ticket') ?? '';

  const details = useQuery({
    queryKey: ['oauth', 'ticket', ticket],
    queryFn: () => api.get<Ticket>(`/auth/oauth/ticket?ticket=${encodeURIComponent(ticket)}`, { anonymous: true }),
    enabled: ticket !== '',
    retry: false,
  });

  const [username, setUsername] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  /*
   * The suggestion arrives after the first render, so it is applied once the
   * query settles - and only into an empty field, which is what stops it
   * overwriting somebody who started typing before it landed.
   */
  useEffect(() => {
    const suggested = details.data?.suggested ?? '';

    if (suggested !== '') setUsername((current) => (current === '' ? suggested : current));
  }, [details.data?.suggested]);

  // Somebody can arrive through a friend's link and then sign in with Discord.
  const [referrer] = useState(() => pendingReferral(search));

  async function submit(event: FormEvent) {
    event.preventDefault();
    errors.clear();
    setBusy(true);

    try {
      const session = await api.post<LoginResponse & { handoff?: string }>(
        '/auth/oauth/complete',
        { ticket, username, accept_terms: accepted, ref: referrer },
        { anonymous: true },
      );

      clearReferral();
      adopt(session);

      /*
       * A signup that began in the desktop app is being finished in a browser,
       * so the account exists but the app still knows nothing about it. The
       * server hands back a one-time code for exactly that, and the return
       * route is the same one a returning account comes through.
       */
      if (typeof session.handoff === 'string' && session.handoff !== '') {
        navigate(`/desktop-return?code=${encodeURIComponent(session.handoff)}`, { replace: true });

        return;
      }

      /*
       * Straight in. Unlike registering with a password there is no address to
       * confirm - Discord confirmed it - so a "check your email" screen would
       * be asking for a step that does not exist.
       */
      navigate('/', { replace: true });
    } catch (error) {
      errors.capture(error);
    } finally {
      setBusy(false);
    }
  }

  if (ticket === '' || details.isError) {
    return (
      <AuthLayout
        title="That sign-up expired"
        subtitle="These links are good for fifteen minutes."
        footer={
          <>
            Start again from <Link to="/login" className="text-accent hover:underline">the sign-in page</Link>.
          </>
        }
      >
        <Alert tone="info">
          Nothing was created and nothing was lost. Pressing Continue with Discord again picks up where this left off.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="One thing left"
      subtitle={`Pick the name you want on ${brand.name}. This is your profile address, and it is permanent.`}
      stage={{
        headline: 'Everybody here has a face.',
        pitch:
          'Yours is one of them the moment you join, built rather than uploaded, and it is how a room sees you when you turn up to listen.',
      }}
      footer={
        <>
          Already have an account? <Link to="/login" className="text-accent hover:underline">Sign in</Link>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {errors.message && <Alert tone="error">{errors.message}</Alert>}

        {/*
          Whose Discord this is. Said out loud because somebody who has just been
          bounced through two redirects deserves to see that they landed in the
          right place, and because it is the only chance to notice they are
          signed in to the wrong Discord account.
        */}
        {details.data !== undefined && (
          <p className="text-[13px] leading-relaxed text-soft" data-oauth-identity={details.data.handle ?? ''}>
            Connected to Discord as <span className="text-ink">{details.data.handle ?? details.data.name}</span>
            {details.data.email !== null && (
              <>
                {' '}
                <span className="text-faint">({details.data.email})</span>
              </>
            )}
            . Not you?{' '}
            <Link to="/login" className="text-accent hover:underline">
              Start again
            </Link>
            .
          </p>
        )}

        <Field
          label="Username"
          name="username"
          autoComplete="username"
          autoFocus
          required
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          hint="Letters, numbers, underscore or hyphen. This is your profile address."
          error={errors.fields.username}
          data-oauth-username
        />

        {referrer !== null && (
          <p className="text-[13px] leading-relaxed text-soft" data-invited-by={referrer}>
            Invited by{' '}
            <Link to={`/${referrer}`} target="_blank" className="text-accent hover:underline">
              @{referrer}
            </Link>
            . They get the credit as soon as you finish.
          </p>
        )}

        <label className="flex cursor-pointer items-start gap-3 text-[13px] leading-relaxed text-soft">
          <input
            type="checkbox"
            checked={accepted}
            data-accept-terms
            onChange={(event) => setAccepted(event.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
          />
          <span>
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-accent hover:underline">
              Terms of Service
            </Link>{' '}
            and the{' '}
            <Link to="/privacy" target="_blank" className="text-accent hover:underline">
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        {errors.fields.accept_terms !== undefined && (
          <p className="text-[13px] text-negative">{errors.fields.accept_terms}</p>
        )}

        <Button type="submit" loading={busy} full disabled={!accepted}>
          Create my account
        </Button>
      </form>
    </AuthLayout>
  );
}
